import React, { Component } from 'react';
import {
  Container, Row, Col, Card, CardBody, CardTitle,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Table, Button, Alert, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input
} from 'reactstrap';
import { Navigate } from 'react-router-dom';

export class AdminPage extends Component {
  static displayName = AdminPage.name;

  constructor(props) {
    super(props);
    this.state = {
      user: null,
      redirectToLogin: false,
      loading: true,
      error: null,
      success: null,
      activeTab: 'products',

      // Data
      products: [],
      sizes: [],
      milkOptions: [],
      extras: [],
      users: [],

      // Modal states
      modalOpen: false,
      modalType: null, // 'product', 'size', 'milk', 'extra', 'user'
      modalMode: 'create', // 'create' or 'edit'
      editingItem: null,

      // Form data
      formData: {}
    };
  }

  componentDidMount() {
    this.loadUser();
  }

  loadUser() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        // Check if user has admin role (role = 2)
        if (user.role !== 2) {
          this.setState({ redirectToLogin: true, error: 'Access denied. Admin role required.' });
        } else {
          this.setState({ user }, () => {
            this.loadAllData();
          });
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
        this.setState({ redirectToLogin: true });
      }
    } else {
      this.setState({ redirectToLogin: true });
    }
  }

  async loadAllData() {
    await Promise.all([
      this.loadProducts(),
      this.loadSizes(),
      this.loadMilkOptions(),
      this.loadExtras(),
      this.loadUsers()
    ]);
    this.setState({ loading: false });
  }

  async loadProducts() {
    const { user } = this.state;
    try {
      const response = await fetch('api/product/products?includeUnavailable=true', {
        headers: { 'X-User-Id': user.userId }
      });
      if (response.ok) {
        const products = await response.json();
        this.setState({ products });
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  }

  async loadSizes() {
    const { user } = this.state;
    try {
      const response = await fetch('api/product/sizes?includeUnavailable=true', {
        headers: { 'X-User-Id': user.userId }
      });
      if (response.ok) {
        const sizes = await response.json();
        this.setState({ sizes });
      }
    } catch (error) {
      console.error('Error loading sizes:', error);
    }
  }

  async loadMilkOptions() {
    const { user } = this.state;
    try {
      const response = await fetch('api/product/milk-options?includeUnavailable=true', {
        headers: { 'X-User-Id': user.userId }
      });
      if (response.ok) {
        const milkOptions = await response.json();
        this.setState({ milkOptions });
      }
    } catch (error) {
      console.error('Error loading milk options:', error);
    }
  }

  async loadExtras() {
    const { user } = this.state;
    try {
      const response = await fetch('api/product/extras?includeUnavailable=true', {
        headers: { 'X-User-Id': user.userId }
      });
      if (response.ok) {
        const extras = await response.json();
        this.setState({ extras });
      }
    } catch (error) {
      console.error('Error loading extras:', error);
    }
  }

  async loadUsers() {
    const { user } = this.state;
    try {
      const response = await fetch('api/auth/users', {
        headers: { 'X-User-Id': user.userId }
      });
      if (response.ok) {
        const users = await response.json();
        this.setState({ users });
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }

  setActiveTab = (tab) => {
    this.setState({ activeTab: tab });
  }

  openModal = (type, mode = 'create', item = null) => {
    let formData = {};

    if (mode === 'edit' && item) {
      formData = { ...item };
    } else {
      // Default values for new items
      switch (type) {
        case 'product':
          formData = {
            name: '', description: '', category: 'Espresso', basePrice: 0,
            isAvailable: true, sortOrder: 0,
            allowTemperatureChoice: true, allowMilkChoice: true,
            allowDecafChoice: true, allowSizeChoice: true
          };
          break;
        case 'size':
          formData = {
            name: '', abbreviation: '', priceModifier: 0,
            sortOrder: 0, isDefault: false, isAvailable: true
          };
          break;
        case 'milk':
          formData = {
            name: '', priceModifier: 0, sortOrder: 0,
            isDefault: false, isAvailable: true, isDairyFree: false
          };
          break;
        case 'extra':
          formData = {
            name: '', category: 'Syrups', price: 0,
            sortOrder: 0, isAvailable: true
          };
          break;
        case 'user':
          formData = { role: item?.role || 0 };
          break;
        default:
          break;
      }
    }

    this.setState({
      modalOpen: true,
      modalType: type,
      modalMode: mode,
      editingItem: item,
      formData
    });
  }

  closeModal = () => {
    this.setState({
      modalOpen: false,
      modalType: null,
      modalMode: 'create',
      editingItem: null,
      formData: {}
    });
  }

  handleFormChange = (field, value) => {
    this.setState(prev => ({
      formData: { ...prev.formData, [field]: value }
    }));
  }

  handleSubmit = async () => {
    const { user, modalType, modalMode, editingItem, formData } = this.state;

    let url = '';
    let method = modalMode === 'create' ? 'POST' : 'PUT';

    switch (modalType) {
      case 'product':
        url = modalMode === 'create' ? 'api/product/products' : `api/product/products/${editingItem.id}`;
        break;
      case 'size':
        url = modalMode === 'create' ? 'api/product/sizes' : `api/product/sizes/${editingItem.id}`;
        break;
      case 'milk':
        url = modalMode === 'create' ? 'api/product/milk-options' : `api/product/milk-options/${editingItem.id}`;
        break;
      case 'extra':
        url = modalMode === 'create' ? 'api/product/extras' : `api/product/extras/${editingItem.id}`;
        break;
      default:
        return;
    }

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user.userId
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        this.setState({
          success: `${modalType.charAt(0).toUpperCase() + modalType.slice(1)} ${modalMode === 'create' ? 'created' : 'updated'} successfully!`,
          error: null
        });
        this.closeModal();
        await this.loadAllData();
      } else {
        const data = await response.json();
        this.setState({ error: data.message || 'Operation failed' });
      }
    } catch (error) {
      console.error('Error:', error);
      this.setState({ error: 'Operation failed' });
    }
  }

  handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    const { user } = this.state;
    let url = '';

    switch (type) {
      case 'product':
        url = `api/product/products/${id}`;
        break;
      case 'size':
        url = `api/product/sizes/${id}`;
        break;
      case 'milk':
        url = `api/product/milk-options/${id}`;
        break;
      case 'extra':
        url = `api/product/extras/${id}`;
        break;
      default:
        return;
    }

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'X-User-Id': user.userId }
      });

      if (response.ok) {
        this.setState({ success: 'Item deleted successfully!', error: null });
        await this.loadAllData();
      } else {
        this.setState({ error: 'Failed to delete item' });
      }
    } catch (error) {
      console.error('Error:', error);
      this.setState({ error: 'Failed to delete item' });
    }
  }

  handleSeedData = async () => {
    if (!window.confirm('This will seed default catalog data. Continue?')) {
      return;
    }

    const { user } = this.state;

    try {
      const response = await fetch('api/product/seed', {
        method: 'POST',
        headers: { 'X-User-Id': user.userId }
      });

      if (response.ok) {
        this.setState({ success: 'Catalog seeded successfully!', error: null });
        await this.loadAllData();
      } else {
        const data = await response.json();
        this.setState({ error: data.message || 'Failed to seed catalog' });
      }
    } catch (error) {
      console.error('Error:', error);
      this.setState({ error: 'Failed to seed catalog' });
    }
  }

  handleUserRoleUpdate = async () => {
    const { user, editingItem, formData } = this.state;

    try {
      const response = await fetch(`api/auth/user/${editingItem.id}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user.userId
        },
        body: JSON.stringify({ role: parseInt(formData.role) })
      });

      if (response.ok) {
        this.setState({
          success: `User role updated successfully!`,
          error: null
        });
        this.closeModal();
        await this.loadUsers();
      } else {
        const data = await response.json();
        this.setState({ error: data.message || 'Failed to update user role' });
      }
    } catch (error) {
      console.error('Error:', error);
      this.setState({ error: 'Failed to update user role' });
    }
  }

  renderProductsTab() {
    const { products } = this.state;

    // Group by category
    const categories = [...new Set(products.map(p => p.category))];

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>Products</h4>
          <Button color="primary" onClick={() => this.openModal('product', 'create')}>
            Add Product
          </Button>
        </div>

        {categories.length === 0 ? (
          <Alert color="info">
            No products yet.
            <Button color="link" onClick={this.handleSeedData}>Seed default catalog</Button>
          </Alert>
        ) : (
          categories.map(category => (
            <div key={category} className="mb-4">
              <h5 className="text-muted mb-3">{category}</h5>
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Base Price</th>
                    <th>Options</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.filter(p => p.category === category).map(product => (
                    <tr key={product.id}>
                      <td><strong>{product.name}</strong></td>
                      <td><small>{product.description}</small></td>
                      <td>${product.basePrice.toFixed(2)}</td>
                      <td>
                        {product.allowSizeChoice && <Badge color="info" className="me-1">Size</Badge>}
                        {product.allowTemperatureChoice && <Badge color="warning" className="me-1">Temp</Badge>}
                        {product.allowMilkChoice && <Badge color="secondary" className="me-1">Milk</Badge>}
                        {product.allowDecafChoice && <Badge color="dark" className="me-1">Decaf</Badge>}
                      </td>
                      <td>
                        <Badge color={product.isAvailable ? 'success' : 'danger'}>
                          {product.isAvailable ? 'Available' : 'Unavailable'}
                        </Badge>
                      </td>
                      <td>
                        <Button size="sm" color="info" className="me-1" onClick={() => this.openModal('product', 'edit', product)}>
                          Edit
                        </Button>
                        <Button size="sm" color="danger" outline onClick={() => this.handleDelete('product', product.id)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))
        )}
      </div>
    );
  }

  renderSizesTab() {
    const { sizes } = this.state;

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>Sizes</h4>
          <Button color="primary" onClick={() => this.openModal('size', 'create')}>
            Add Size
          </Button>
        </div>

        <Table responsive striped>
          <thead>
            <tr>
              <th>Name</th>
              <th>Abbreviation</th>
              <th>Price Modifier</th>
              <th>Default</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sizes.map(size => (
              <tr key={size.id}>
                <td><strong>{size.name}</strong></td>
                <td>{size.abbreviation}</td>
                <td>{size.priceModifier > 0 ? `+$${size.priceModifier.toFixed(2)}` : 'Base'}</td>
                <td>{size.isDefault && <Badge color="primary">Default</Badge>}</td>
                <td>
                  <Badge color={size.isAvailable ? 'success' : 'danger'}>
                    {size.isAvailable ? 'Available' : 'Unavailable'}
                  </Badge>
                </td>
                <td>
                  <Button size="sm" color="info" className="me-1" onClick={() => this.openModal('size', 'edit', size)}>
                    Edit
                  </Button>
                  <Button size="sm" color="danger" outline onClick={() => this.handleDelete('size', size.id)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  renderMilkOptionsTab() {
    const { milkOptions } = this.state;

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>Milk Options</h4>
          <Button color="primary" onClick={() => this.openModal('milk', 'create')}>
            Add Milk Option
          </Button>
        </div>

        <Table responsive striped>
          <thead>
            <tr>
              <th>Name</th>
              <th>Price Modifier</th>
              <th>Dairy-Free</th>
              <th>Default</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {milkOptions.map(option => (
              <tr key={option.id}>
                <td><strong>{option.name}</strong></td>
                <td>{option.priceModifier > 0 ? `+$${option.priceModifier.toFixed(2)}` : 'Free'}</td>
                <td>{option.isDairyFree && <Badge color="success">Dairy-Free</Badge>}</td>
                <td>{option.isDefault && <Badge color="primary">Default</Badge>}</td>
                <td>
                  <Badge color={option.isAvailable ? 'success' : 'danger'}>
                    {option.isAvailable ? 'Available' : 'Unavailable'}
                  </Badge>
                </td>
                <td>
                  <Button size="sm" color="info" className="me-1" onClick={() => this.openModal('milk', 'edit', option)}>
                    Edit
                  </Button>
                  <Button size="sm" color="danger" outline onClick={() => this.handleDelete('milk', option.id)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  renderExtrasTab() {
    const { extras } = this.state;

    // Group by category
    const categories = [...new Set(extras.map(e => e.category))];

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>Extras & Add-ons</h4>
          <Button color="primary" onClick={() => this.openModal('extra', 'create')}>
            Add Extra
          </Button>
        </div>

        {categories.map(category => (
          <div key={category} className="mb-4">
            <h5 className="text-muted mb-3">{category}</h5>
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {extras.filter(e => e.category === category).map(extra => (
                  <tr key={extra.id}>
                    <td><strong>{extra.name}</strong></td>
                    <td>{extra.price > 0 ? `+$${extra.price.toFixed(2)}` : 'Free'}</td>
                    <td>
                      <Badge color={extra.isAvailable ? 'success' : 'danger'}>
                        {extra.isAvailable ? 'Available' : 'Unavailable'}
                      </Badge>
                    </td>
                    <td>
                      <Button size="sm" color="info" className="me-1" onClick={() => this.openModal('extra', 'edit', extra)}>
                        Edit
                      </Button>
                      <Button size="sm" color="danger" outline onClick={() => this.handleDelete('extra', extra.id)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ))}
      </div>
    );
  }

  renderUsersTab() {
    const { users, user } = this.state;

    const getRoleName = (role) => {
      switch (role) {
        case 0: return 'Customer';
        case 1: return 'Service';
        case 2: return 'Admin';
        default: return 'Unknown';
      }
    };

    const getRoleBadgeColor = (role) => {
      switch (role) {
        case 0: return 'secondary';
        case 1: return 'info';
        case 2: return 'danger';
        default: return 'secondary';
      }
    };

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>User Management</h4>
        </div>

        <Table responsive striped>
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Balance</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td>
                  <strong>{u.username}</strong>
                  {u.id === user.userId && <Badge color="primary" className="ms-2">You</Badge>}
                </td>
                <td>{u.email}</td>
                <td>
                  <Badge color={getRoleBadgeColor(u.role)}>
                    {getRoleName(u.role)}
                  </Badge>
                </td>
                <td>${(u.balance || 0).toFixed(2)}</td>
                <td>
                  {u.id !== user.userId ? (
                    <Button size="sm" color="info" onClick={() => this.openModal('user', 'edit', u)}>
                      Change Role
                    </Button>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {users.length === 0 && (
          <Alert color="info">No users found.</Alert>
        )}
      </div>
    );
  }

  renderModal() {
    const { modalOpen, modalType, modalMode, formData } = this.state;

    const title = modalType === 'user'
      ? 'Change User Role'
      : `${modalMode === 'create' ? 'Add' : 'Edit'} ${modalType ? modalType.charAt(0).toUpperCase() + modalType.slice(1) : ''}`;

    return (
      <Modal isOpen={modalOpen} toggle={this.closeModal} size="lg">
        <ModalHeader toggle={this.closeModal}>{title}</ModalHeader>
        <ModalBody>
          {modalType === 'product' && (
            <Form>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Name</Label>
                    <Input
                      value={formData.name || ''}
                      onChange={(e) => this.handleFormChange('name', e.target.value)}
                      placeholder="e.g., Latte"
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Category</Label>
                    <Input
                      type="select"
                      value={formData.category || 'Espresso'}
                      onChange={(e) => this.handleFormChange('category', e.target.value)}
                    >
                      <option value="Espresso">Espresso</option>
                      <option value="Brewed">Brewed</option>
                      <option value="Tea">Tea</option>
                      <option value="Other">Other</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Description</Label>
                <Input
                  type="textarea"
                  value={formData.description || ''}
                  onChange={(e) => this.handleFormChange('description', e.target.value)}
                  placeholder="A short description of the drink"
                />
              </FormGroup>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label>Base Price ($)</Label>
                    <Input
                      type="number"
                      step="0.25"
                      min="0"
                      value={formData.basePrice || 0}
                      onChange={(e) => this.handleFormChange('basePrice', parseFloat(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>Sort Order</Label>
                    <Input
                      type="number"
                      value={formData.sortOrder || 0}
                      onChange={(e) => this.handleFormChange('sortOrder', parseInt(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup check className="mt-4">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={formData.isAvailable !== false}
                        onChange={(e) => this.handleFormChange('isAvailable', e.target.checked)}
                      />
                      Available
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
              <hr />
              <p className="text-muted">Customization Options</p>
              <Row>
                <Col md="3">
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={formData.allowSizeChoice !== false}
                        onChange={(e) => this.handleFormChange('allowSizeChoice', e.target.checked)}
                      />
                      Size
                    </Label>
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={formData.allowTemperatureChoice !== false}
                        onChange={(e) => this.handleFormChange('allowTemperatureChoice', e.target.checked)}
                      />
                      Temperature
                    </Label>
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={formData.allowMilkChoice !== false}
                        onChange={(e) => this.handleFormChange('allowMilkChoice', e.target.checked)}
                      />
                      Milk
                    </Label>
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={formData.allowDecafChoice !== false}
                        onChange={(e) => this.handleFormChange('allowDecafChoice', e.target.checked)}
                      />
                      Decaf
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          )}

          {modalType === 'size' && (
            <Form>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Name</Label>
                    <Input
                      value={formData.name || ''}
                      onChange={(e) => this.handleFormChange('name', e.target.value)}
                      placeholder="e.g., Medium"
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Abbreviation</Label>
                    <Input
                      value={formData.abbreviation || ''}
                      onChange={(e) => this.handleFormChange('abbreviation', e.target.value)}
                      placeholder="e.g., M"
                      maxLength="3"
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label>Price Modifier ($)</Label>
                    <Input
                      type="number"
                      step="0.25"
                      value={formData.priceModifier || 0}
                      onChange={(e) => this.handleFormChange('priceModifier', parseFloat(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>Sort Order</Label>
                    <Input
                      type="number"
                      value={formData.sortOrder || 0}
                      onChange={(e) => this.handleFormChange('sortOrder', parseInt(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup check className="mt-4">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={formData.isDefault || false}
                        onChange={(e) => this.handleFormChange('isDefault', e.target.checked)}
                      />
                      Default
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={formData.isAvailable !== false}
                    onChange={(e) => this.handleFormChange('isAvailable', e.target.checked)}
                  />
                  Available
                </Label>
              </FormGroup>
            </Form>
          )}

          {modalType === 'milk' && (
            <Form>
              <FormGroup>
                <Label>Name</Label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => this.handleFormChange('name', e.target.value)}
                  placeholder="e.g., Oat Milk"
                />
              </FormGroup>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label>Price Modifier ($)</Label>
                    <Input
                      type="number"
                      step="0.25"
                      value={formData.priceModifier || 0}
                      onChange={(e) => this.handleFormChange('priceModifier', parseFloat(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>Sort Order</Label>
                    <Input
                      type="number"
                      value={formData.sortOrder || 0}
                      onChange={(e) => this.handleFormChange('sortOrder', parseInt(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup check className="mt-4">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={formData.isDairyFree || false}
                        onChange={(e) => this.handleFormChange('isDairyFree', e.target.checked)}
                      />
                      Dairy-Free
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={formData.isDefault || false}
                        onChange={(e) => this.handleFormChange('isDefault', e.target.checked)}
                      />
                      Default
                    </Label>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={formData.isAvailable !== false}
                        onChange={(e) => this.handleFormChange('isAvailable', e.target.checked)}
                      />
                      Available
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          )}

          {modalType === 'extra' && (
            <Form>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Name</Label>
                    <Input
                      value={formData.name || ''}
                      onChange={(e) => this.handleFormChange('name', e.target.value)}
                      placeholder="e.g., Vanilla Syrup"
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Category</Label>
                    <Input
                      type="select"
                      value={formData.category || 'Syrups'}
                      onChange={(e) => this.handleFormChange('category', e.target.value)}
                    >
                      <option value="Shots">Shots</option>
                      <option value="Syrups">Syrups</option>
                      <option value="Toppings">Toppings</option>
                      <option value="Other">Other</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label>Price ($)</Label>
                    <Input
                      type="number"
                      step="0.25"
                      min="0"
                      value={formData.price || 0}
                      onChange={(e) => this.handleFormChange('price', parseFloat(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>Sort Order</Label>
                    <Input
                      type="number"
                      value={formData.sortOrder || 0}
                      onChange={(e) => this.handleFormChange('sortOrder', parseInt(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup check className="mt-4">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={formData.isAvailable !== false}
                        onChange={(e) => this.handleFormChange('isAvailable', e.target.checked)}
                      />
                      Available
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          )}

          {modalType === 'user' && (
            <Form>
              <p className="mb-3">
                Changing role for: <strong>{this.state.editingItem?.username}</strong>
              </p>
              <FormGroup>
                <Label>Role</Label>
                <Input
                  type="select"
                  value={formData.role}
                  onChange={(e) => this.handleFormChange('role', parseInt(e.target.value))}
                >
                  <option value="0">Customer</option>
                  <option value="1">Service</option>
                  <option value="2">Admin</option>
                </Input>
              </FormGroup>
              <Alert color="warning" className="mt-3">
                <strong>Note:</strong> Changing a user's role will affect their access permissions immediately.
              </Alert>
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          {modalType === 'user' ? (
            <Button color="primary" onClick={this.handleUserRoleUpdate}>
              Update Role
            </Button>
          ) : (
            <Button color="primary" onClick={this.handleSubmit}>
              {modalMode === 'create' ? 'Create' : 'Save Changes'}
            </Button>
          )}
          <Button color="secondary" onClick={this.closeModal}>Cancel</Button>
        </ModalFooter>
      </Modal>
    );
  }

  render() {
    const { user, redirectToLogin, loading, error, success, activeTab, products, users } = this.state;

    if (redirectToLogin) {
      return <Navigate to="/login" state={{ error }} />;
    }

    if (loading) {
      return <Container><p>Loading...</p></Container>;
    }

    return (
      <Container className="admin-page">
        <div className="text-center mb-4">
          <h1 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>
            Admin Dashboard
          </h1>
          <p className="lead" style={{ maxWidth: '500px', margin: '0 auto' }}>
            Manage products, options, and catalog settings
          </p>
        </div>

        {error && (
          <Alert color="danger" toggle={() => this.setState({ error: null })}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="success" toggle={() => this.setState({ success: null })}>
            {success}
          </Alert>
        )}

        {/* Quick Stats */}
        <Row className="mb-4">
          <Col>
            <Card className="text-center">
              <CardBody>
                <h3 style={{ color: 'var(--moss-dark)' }}>{products.length}</h3>
                <p className="mb-0 text-muted">Products</p>
              </CardBody>
            </Card>
          </Col>
          <Col>
            <Card className="text-center">
              <CardBody>
                <h3 style={{ color: 'var(--moss-dark)' }}>{this.state.sizes.length}</h3>
                <p className="mb-0 text-muted">Sizes</p>
              </CardBody>
            </Card>
          </Col>
          <Col>
            <Card className="text-center">
              <CardBody>
                <h3 style={{ color: 'var(--moss-dark)' }}>{this.state.milkOptions.length}</h3>
                <p className="mb-0 text-muted">Milk Options</p>
              </CardBody>
            </Card>
          </Col>
          <Col>
            <Card className="text-center">
              <CardBody>
                <h3 style={{ color: 'var(--moss-dark)' }}>{this.state.extras.length}</h3>
                <p className="mb-0 text-muted">Extras</p>
              </CardBody>
            </Card>
          </Col>
          <Col>
            <Card className="text-center">
              <CardBody>
                <h3 style={{ color: 'var(--moss-dark)' }}>{users.length}</h3>
                <p className="mb-0 text-muted">Users</p>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Seed Data Button */}
        {products.length === 0 && (
          <Alert color="info" className="text-center">
            <p className="mb-2">Your catalog is empty. Would you like to start with a default coffee shop menu?</p>
            <Button color="primary" onClick={this.handleSeedData}>
              Seed Default Catalog
            </Button>
          </Alert>
        )}

        {/* Navigation Tabs */}
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={activeTab === 'products' ? 'active' : ''}
              onClick={() => this.setActiveTab('products')}
              style={{ cursor: 'pointer' }}
            >
              Products
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'sizes' ? 'active' : ''}
              onClick={() => this.setActiveTab('sizes')}
              style={{ cursor: 'pointer' }}
            >
              Sizes
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'milk' ? 'active' : ''}
              onClick={() => this.setActiveTab('milk')}
              style={{ cursor: 'pointer' }}
            >
              Milk Options
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'extras' ? 'active' : ''}
              onClick={() => this.setActiveTab('extras')}
              style={{ cursor: 'pointer' }}
            >
              Extras
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'users' ? 'active' : ''}
              onClick={() => this.setActiveTab('users')}
              style={{ cursor: 'pointer' }}
            >
              Users
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="products">
            {this.renderProductsTab()}
          </TabPane>
          <TabPane tabId="sizes">
            {this.renderSizesTab()}
          </TabPane>
          <TabPane tabId="milk">
            {this.renderMilkOptionsTab()}
          </TabPane>
          <TabPane tabId="extras">
            {this.renderExtrasTab()}
          </TabPane>
          <TabPane tabId="users">
            {this.renderUsersTab()}
          </TabPane>
        </TabContent>

        {this.renderModal()}
      </Container>
    );
  }
}
